<template>
  <component
    :is="resolvedComponent"
    v-if="resolvedComponent"
    v-bind="link?.[0]"
    :class="buttonClasses"
  >
    {{ text }} <span v-if="style === 'text-callout'">→</span>
  </component>
</template>
<script setup lang="ts">
import type { Button } from '~/sanity/types';
import InternalLink from '../InternalLink/InternalLink.vue';
import ExternalLink from '../ExternalLink/ExternalLink.vue';
import PDFLink from '~/components/Atmos/PDFLink/PDFLink.vue';
import DriftAction from '../DriftAction/DriftAction.vue';

const props = defineProps<{ buttonProps: Button }>();
const { text, style, link, _type } = toRefs(reactive(props.buttonProps));

const resolvedComponent = computed(() => {
  if (link?.value?.[0]?._type === 'internalLink') return InternalLink;
  if (link?.value?.[0]?._type === 'externalLink') return ExternalLink;
  if (link?.value?.[0]?._type === 'pdfLink') return PDFLink;
  if (link?.value?.[0]?._type === 'driftAction') return DriftAction;
  if (_type.value === 'button') return 'button';
  return null;
});
const buttonClasses = computed(() => ({
  [style?.value ? buttonStyles[style.value] : 'primary']: true,
  'btn-link rounded px-4 text-center font-ptc-Rajdhani lg:text-18px lg:leading-52px text-15px leading-10 font-bold uppercase transition-all duration-300 ':
    true,
  'box-border border-3px': style?.value != 'text-callout',
}));
const buttonStyles: Record<string, string> = {
  primary:
    'border-3px border-www-color-transparent bg-www-color-primary-lighter text-www-color-white hover:bg-www-color-primary focus:border-www-color-accent-blue-light focus:bg-www-color-primary active:bg-www-color-primary active:text-www-color-primary-lighter',
  'primary-alt':
    'border-3px border-www-color-primary-lighter bg-www-color-white text-www-color-primary-lighter hover:bg-www-color-primary-lighter hover:text-www-color-white focus:border-3px focus:border-www-color-accent-blue-light focus:bg-www-color-primary-lighter focus:text-www-color-primary-dark active:text-www-color-primary',
  transparent:
    'border-3px border-www-color-primary-lighter bg-www-color-transparent hover:bg-www-color-white hover:border-www-color-primary-light hover:text-www-color-primary-lighter focus:outline focus:outline-www-color-accent-blue-light active:border-www-color-primary-light active:text-www-color-primary-lighter',
  secondary:
    'border-3px border-www-color-transparent bg-www-color-neutral-medium text-www-color-white hover:bg-www-color-neutral-medium-dark focus:border-www-color-accent-blue-light active:bg-www-color-neutral-medium-dark active:text-www-color-neutral-medium',
  'secondary-alt':
    'border-3px border-www-color-neutral-medium bg-www-color-white text-www-color-neutral-medium hover:bg-www-color-neutral-medium hover:text-www-color-white focus:border-3px focus:border-www-color-accent-blue-light active:bg-www-color-neutral-medium active:text-www-color-neutral-medium-dark',
  tertiary:
    'border-3px border-www-color-transparent bg-www-color-accent-blue-pale text-www-color-neutral-medium-dark hover:border-www-color-accent-blue-light hover:bg-www-color-accent-blue-light hover:text-www-color-white focus:border-www-color-accent-blue-light focus:bg-www-color-accent-blue-light focus:text-www-color-white active:bg-www-color-accent-blue-light active:text-www-color-accent-blue-pale',
  disabled:
    'border-none cursor-not-allowed bg-www-color-neutral-off-white text-www-color-neutral-light',
  'text-callout': 'text-callout text-20px  leading-30px no-underline',
};
</script>
<style>
.text-callout {
  color: var(--www-color-primary);
}
.bg-wrapper.text-www-color-white .text-callout {
  color: var(--www-color-accent-orange-light);
}
</style>
